import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { markdown } from '@codemirror/lang-markdown';
import { python } from '@codemirror/lang-python';
import { yaml } from '@codemirror/lang-yaml';
import { html } from '@codemirror/lang-html';
import {
  defaultHighlightStyle,
  syntaxHighlighting,
} from '@codemirror/language';
import { highlightSelectionMatches, searchKeymap } from '@codemirror/search';
import { EditorState, StateField, StateEffect, RangeSet } from '@codemirror/state';
import {
  Decoration,
  type DecorationSet,
  EditorView,
  highlightActiveLine,
  keymap,
  lineNumbers,
} from '@codemirror/view';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { slashCommandPlugin } from './slash-command/extension';
import { SlashCommandMenu, type SlashCommandItem } from './slash-command/menu';

interface CodeEditorProps {
  value: string;
  onChange?: (value: string) => void;
  language?: string;
  readOnly?: boolean;
  className?: string;
  // Line number to highlight as error (1-indexed), null to clear
  errorLine?: number | null;
  // Callback for handling slash command item selection that requires UI (e.g., upload, skill select)
  onSlashCommand?: (
    item: SlashCommandItem,
    insert: (text: string) => void
  ) => void;
}

// Error line highlighting
const setErrorLine = StateEffect.define<number | null>();

const errorLineMark = Decoration.line({ class: 'cm-errorLine' });

const errorLineField = StateField.define<DecorationSet>({
  create() {
    return RangeSet.empty;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setErrorLine)) {
        if (effect.value === null) {
          return RangeSet.empty;
        }
        const lineNum = effect.value;
        if (lineNum >= 1 && lineNum <= tr.state.doc.lines) {
          const line = tr.state.doc.line(lineNum);
          return RangeSet.of([errorLineMark.range(line.from)]);
        }
        return RangeSet.empty;
      }
    }
    return decorations.map(tr.changes);
  },
  provide: f => EditorView.decorations.from(f),
});

export interface CodeEditorHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

// Detect language from file extension
export function detectLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  switch (ext) {
    case 'md':
    case 'markdown':
      return 'markdown';
    case 'yaml':
    case 'yml':
      return 'yaml';
    case 'json':
      return 'json';
    case 'py':
    case 'python':
      return 'python';
    case 'html':
      return 'html';
    default:
      return 'text';
  }
}

function getLanguageExtension(language: string) {
  switch (language) {
    case 'markdown':
      return markdown();
    case 'yaml':
      return yaml();
    case 'json':
      return json();
    case 'python':
      return python();
    case 'html':
      return html();
    default:
      return [];
  }
}

export const CodeEditor = forwardRef<CodeEditorHandle, CodeEditorProps>(
  (
    {
      value,
      onChange,
      language = 'text',
      readOnly = false,
      className = '',
      errorLine,
      onSlashCommand,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewRef = useRef<EditorView | null>(null);
    const onChangeRef = useRef(onChange);
    const [slashState, setSlashState] = useState({
      open: false,
      from: 0,
      to: 0,
      filter: '',
      top: 0,
      left: 0,
    });

    // Keep onChange ref updated
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    // Handle slash command selection
    const handleSlashSelect = useCallback(
      (item: SlashCommandItem) => {
        const view = viewRef.current;
        if (!view) return;

        // Basic text insertions we can handle directly
        let textToInsert = '';
        let cursorOffset = 0;

        switch (item.id) {
          case 'h2':
            textToInsert = '## ';
            break;
          case 'h3':
            textToInsert = '### ';
            break;
          case 'bullet-list':
            textToInsert = '- ';
            break;
          case 'numbered-list':
            textToInsert = '1. ';
            break;
          case 'quote':
            textToInsert = '> ';
            break;
          case 'divider':
            textToInsert = '---\n';
            break;
          case 'code':
            textToInsert = '```\n\n```';
            cursorOffset = -4; // Position cursor inside block
            break;
          default:
            // For complex items (media, components), defer to parent via callback
            if (onSlashCommand) {
              // Close menu first
              setSlashState(prev => ({ ...prev, open: false }));

              // Define inserter function for parent to call
              const insert = (text: string) => {
                view.dispatch({
                  changes: {
                    from: slashState.from,
                    to: slashState.to,
                    insert: text,
                  },
                  selection: { anchor: slashState.from + text.length },
                });
                view.focus();
              };

              onSlashCommand(item, insert);
              return;
            }
        }

        if (textToInsert) {
          view.dispatch({
            changes: {
              from: slashState.from,
              to: slashState.to,
              insert: textToInsert,
            },
            selection: {
              anchor: slashState.from + textToInsert.length + cursorOffset,
            },
          });
          view.focus();
          setSlashState(prev => ({ ...prev, open: false }));
        }
      },
      [slashState, onSlashCommand]
    );

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        insertAtCursor: (text: string) => {
          const view = viewRef.current;
          if (!view) return;

          const { from, to } = view.state.selection.main;
          view.dispatch({
            changes: { from, to, insert: text },
            selection: { anchor: from + text.length },
          });
          view.focus();
        },
        focus: () => {
          viewRef.current?.focus();
        },
      }),
      []
    );

    const createUpdateListener = useCallback(() => {
      return EditorView.updateListener.of(update => {
        if (update.docChanged && onChangeRef.current) {
          onChangeRef.current(update.state.doc.toString());
        }
      });
    }, []);

    // Create editor
    useEffect(() => {
      if (!containerRef.current) return;

      const extensions = [
        lineNumbers(),
        highlightActiveLine(),
        highlightSelectionMatches(),
        history(),
        syntaxHighlighting(defaultHighlightStyle),
        keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap]),
        getLanguageExtension(language),
        slashCommandPlugin(setSlashState),
        errorLineField,
        EditorView.lineWrapping,
        EditorView.theme({
          '&': {
            height: '100%',
            fontSize: '13px',
          },
          '&.cm-focused': {
            outline: 'none',
          },
          '.cm-scroller': {
            overflow: 'auto',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          },
          '.cm-content': {
            padding: '12px 0',
          },
          '.cm-gutters': {
            backgroundColor: 'transparent',
            borderRight: '1px solid hsl(var(--border) / 0.5)',
            color: 'hsl(var(--muted-foreground) / 0.7)',
            paddingRight: '8px',
          },
          '.cm-gutterElement': {
            paddingLeft: '12px !important',
          },
          '.cm-activeLineGutter': {
            backgroundColor: 'transparent',
            color: 'hsl(var(--foreground))',
          },
          '.cm-activeLine': {
            backgroundColor: 'hsl(var(--muted) / 0.3)',
          },
          '.cm-selectionBackground': {
            backgroundColor: 'hsl(var(--accent) / 0.3) !important',
          },
          '.cm-cursor': {
            borderLeftColor: 'hsl(var(--foreground))',
          },
          '.cm-errorLine': {
            backgroundColor: 'oklch(from var(--destructive) l c h / 0.15) !important',
            borderLeft: '3px solid var(--destructive) !important',
          },
        }),
      ];

      if (readOnly) {
        extensions.push(EditorState.readOnly.of(true));
      } else {
        extensions.push(createUpdateListener());
      }

      const state = EditorState.create({
        doc: value,
        extensions,
      });

      const view = new EditorView({
        state,
        parent: containerRef.current,
      });

      viewRef.current = view;

      return () => {
        view.destroy();
        viewRef.current = null;
      };
    }, [language, readOnly, createUpdateListener]); // Re-create on language or readOnly change

    // Update content when value changes externally
    useEffect(() => {
      const view = viewRef.current;
      if (!view) return;

      const currentValue = view.state.doc.toString();
      if (currentValue !== value) {
        view.dispatch({
          changes: {
            from: 0,
            to: currentValue.length,
            insert: value,
          },
        });
      }
    }, [value]);

    // Update error line highlighting
    useEffect(() => {
      const view = viewRef.current;
      if (!view) return;

      view.dispatch({
        effects: setErrorLine.of(errorLine ?? null),
      });
    }, [errorLine]);

    return (
      <div
        ref={containerRef}
        className={`overflow-hidden bg-background relative ${className}`}
      >
        <SlashCommandMenu
          open={slashState.open}
          top={slashState.top}
          left={slashState.left}
          filter={slashState.filter}
          onSelect={handleSlashSelect}
          onClose={() => setSlashState(prev => ({ ...prev, open: false }))}
          containerRef={containerRef}
        />
      </div>
    );
  }
);

CodeEditor.displayName = 'CodeEditor';

export default CodeEditor;
